import Vue from "vue";
import Router, { Route } from "vue-router";
import store from "@/store";

import { ZoneNames } from "@/types";

import NotFound from "@/views/NotFound.vue";
import Home from "@/views/Home.vue";
import Tuin from "@/views/Tuin.vue";
import Zone from "@/views/Zone.vue";
import Border from "@/views/Border.vue";
import Plants from "@/views/Plants.vue";
import PlantType from "@/views/PlantType.vue";
import Plant from "@/views/Plant.vue";
import PlantList from "@/views/PlantList.vue";
import PlantListDetails from "@/views/PlantListDetails.vue";
import PlantSearch from "@/views/PlantSearch.vue";
import About from "./views/About.vue";

Vue.use(Router);

const baseRoutes = [
  {
    path: "/",
    name: "home",
    component: Home,
    meta: { beforeRouteChanged: noNav },
  },
  {
    path: "/about",
    name: "about",
    component: About,
    meta: {
      beforeRouteChanged: homeNav,
    },
  },
];

const gardenRoutes = [
  {
    path: "/tuin",
    name: "tuin",
    component: Tuin,
    meta: {
      beforeRouteChanged: homeNav,
    },
  },
  {
    path: "/tuin/:zone",
    name: "zone",
    component: Zone,
    meta: {
      beforeRouteChanged: tuinNav,
    },
  },
  {
    path: "/tuin/:zone/:border",
    name: "border",
    component: Border,
    meta: {
      beforeRouteChanged: zoneNav,
    },
  },
  {
    path: "/tuin/:zone/:border/:plant",
    name: "tuin-plant",
    component: Plant,
    meta: {
      beforeRouteChanged: plantNav,
    },
  },
];

function noNav() {
  store.dispatch("setNavigation", {
    up: null,
    next: null,
    prev: null,
  });
}

function homeNav() {
  store.dispatch("setNavigation", {
    up: { name: "home" },
    next: null,
    prev: null,
  });
}

function tuinNav(to: Route, from: Route, nextFunction: Function) {
  const { zone } = to.params;
  const up = { name: "tuin" };
  const zones = Object.keys(store.state.garden.zones);

  const zoneIndex = zones.indexOf(zone);
  if (zoneIndex == -1) {
    return nextFunction("/404");
  }

  const prevName = zoneIndex > 0 && zones[zoneIndex - 1];
  const prev = prevName && { name: "zone", params: { zone: prevName } };

  const nextName =
    zoneIndex > -1 && zoneIndex < zones.length - 1 && zones[zoneIndex + 1];
  const next = nextName && { name: "zone", params: { zone: nextName } };

  store.dispatch("setNavigation", { prev, up, next });
}

function zoneNav(to: Route, from: Route, nextFunction: Function) {
  const { zone, border } = to.params;
  const up = { name: "zone", params: { zone } };

  const borders = Object.keys(
    store.state.garden.zones[zone as ZoneNames]!.borders,
  );
  const borderIndex = borders.indexOf(border);
  if (borderIndex == -1) {
    return nextFunction("/404");
  }

  const prevName = borderIndex > 0 && borders[borderIndex - 1];
  const prev = prevName && {
    name: "border",
    params: { zone, border: prevName },
  };

  const nextName =
    borderIndex > -1 &&
    borderIndex < borders.length - 1 &&
    borders[borderIndex + 1];
  const next = nextName && {
    name: "border",
    params: { zone, border: nextName },
  };

  store.dispatch("setNavigation", { prev, up, next });
}

const plantRoutes = [
  {
    path: "/planten",
    name: "plants",
    component: Plants,
    meta: { beforeRouteChanged: homeNav },
  },
  {
    path: "/search/:searchText",
    name: "plants-search",
    component: PlantSearch,
    meta: { beforeRouteChanged: plantenNav },
  },
  {
    path: "/planten/:type",
    name: "plants-type",
    component: PlantType,
    meta: { beforeRouteChanged: typeNav },
  },
  {
    path: "/planten/:type/:plant",
    name: "plants-type-plant",
    component: Plant,
    meta: { beforeRouteChanged: plantNav },
  },
  {
    path: "/plant/",
    name: "plant-list",
    component: PlantList,
    meta: { beforeRouteChanged: homeNav },
  },
  {
    path: "/plant/:plant",
    name: "plants-plant",
    component: Plant,
    meta: { beforeRouteChanged: plantNav },
  },
  {
    path: "/plant/:plant/details",
    name: "plant-admin",
    component: PlantListDetails,
    meta: { beforeRouteChanged: plantListNav },
  },
];

function typeNav(to: Route, from: Route, nextFunction: Function) {
  const { type } = to.params;
  const types = store.state.plantTypes;
  const typeIndex = types.indexOf(type);
  if (typeIndex == -1) {
    return nextFunction("/404");
  }
  const up = { name: "plants" };

  const prevName = typeIndex > 0 && types[typeIndex - 1];
  const prev = prevName && {
    name: "plants-type",
    params: { type: prevName },
  };

  const nextName = typeIndex < types.length - 1 && types[typeIndex + 1];
  const next = nextName && {
    name: "plants-type",
    params: { type: nextName },
  };

  store.dispatch("setNavigation", { prev, up, next });
}

function plantenNav(to: Route, from: Route, nextFunction: Function) {
  store.dispatch("setNavigation", {
    up: { name: "plants" },
    next: null,
    prev: null,
  });
}

function plantNav(to: Route, from: Route, nextFunction: Function) {
  const { plant } = to.params;
  if (!store.state.plants[+plant]) {
    return nextFunction("/404");
  }
  const up = { name: from.name, params: from.params };
  const next = null;
  const prev = null;

  store.dispatch("setNavigation", { prev, up, next });
}

function plantListNav(to: Route, from: Route, nextFunction: Function) {
  const { plant } = to.params;
  if (!store.state.plants[+plant]) {
    return nextFunction("/404");
  }
  const up = { name: "plant-list", params: from.params };
  const prev = +plant > 1 && {
    name: "plant-admin",
    params: { plant: +plant - 1 },
  };
  const next = +plant < Object.keys(store.state.plants).length && {
    name: "plant-admin",
    params: { plant: +plant + 1 },
  };

  store.dispatch("setNavigation", { prev, up, next });
}

const router = new Router({
  mode: "history",
  routes: [
    ...baseRoutes,
    ...gardenRoutes,
    ...plantRoutes,
    { path: "/404", component: NotFound },
    { path: "*", redirect: "/404" },
  ],
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  if (to.meta.beforeRouteChanged) {
    to.meta.beforeRouteChanged(to, from, next);
  } else {
    homeNav();
  }

  next();
});

export default router;
